function getOrdinal(day) {
  const s = ['th','st','nd','rd'];
  const v = day % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function formatDate(iso) {
  if (!iso) return 'Not specified';
  const d     = new Date(iso);
  const month = d.toLocaleString('default', { month: 'long' });
  const day   = d.getDate();
  const year  = d.getFullYear();
  return `${month} ${day}${getOrdinal(day)} ${year}`;
}

// Formats a budget given either a structured object or string + currency
function formatBudget(budgetObj, currencyField) {
  if (budgetObj && typeof budgetObj === 'object' && budgetObj.amount != null) {
    const num = Number(String(budgetObj.amount).replace(/,/g, ''));
    if (!isNaN(num) && budgetObj.currency) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: budgetObj.currency
      }).format(num);
    }
  }
  if (typeof budgetObj === 'string' && currencyField) {
    const raw = budgetObj.replace(/,/g, '');
    const num = Number(raw);
    if (!isNaN(num)) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyField
        }).format(num);
      } catch {
        return `${currencyField} ${budgetObj}`;
      }
    }
    return `${currencyField} ${budgetObj}`;
  }
  if (typeof budgetObj === 'string') {
    return budgetObj;
  }
  return 'Not specified';
}

// Render companions from object or simple string
function formatCompanions(c) {
  if (!c) return 'Not specified';
  if (typeof c === 'string') return c;
  const { type, hasKids, count } = c;
  switch (type) {
    case 'Solo':               return 'Solo';
    case 'Other':              return 'Other';
    case 'Partner':
      return hasKids
        ? `Partner with ${count} child${count > 1 ? 'ren' : ''}`
        : 'Partner (no children)';
    case 'Friend Group':       return `${count} friend${count > 1 ? 's' : ''}`;
    case 'Business Associate': return `${count} colleague${count > 1 ? 's' : ''}`;
    default:                   return 'Not specified';
  }
}

// Create the trip prompt based on user data
export default function createTripPrompt(data) {
  const budgetText = formatBudget(data.budget, data.currency);
  // Calculate number of days, cap at 20
  let dayCount = 'N';
  if (data.dates.start && data.dates.end) {
    const start = new Date(data.dates.start);
    const end   = new Date(data.dates.end);
    const diff  = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    dayCount    = diff > 20 ? 20 : diff;
  }

  return `
Trip Info
• From:            ${data.from || 'California'}
• To:              ${data.destination || 'Dubai'}
• Dates:           ${data.dates.start && data.dates.end
                      ? formatDate(data.dates.start) + ' – ' + formatDate(data.dates.end)
                      : '3 days'}
• Budget:          ${budgetText}
• Hotel:           ${data.hasHotelReservation ? 'Yes' : 'No'}
• Interests:       ${data.interests.join(', ') || 'None'}
• Transport:       ${data.transport || 'flight'}
• Companions:      ${formatCompanions(data.companions)}
• Diet:            ${data.diet.join(', ') || 'None'}
• Mood:            ${data.mood.join(', ') || 'None'}
• Experience:      ${data.experience || 'frequent flier'}
• Notes:           ${data.additionalDescription || 'None'}

Generate a day‑by‑day itinerary for Day 1 through Day ${dayCount}:
— For each day, use this structure:
  1. **Theme:** (e.g., culture, adventure)
  2. **Schedule:** Morning / Afternoon / Evening
  3. **Transport:** how to get between spots
  4. **Meals:** dish + price range
  5. **Local Tips:** key phrases & etiquette
  6. **Weather & Packing:** what to expect & pack
  7. **Hidden Gems:** off‑beat suggestions

Then provide a **Destination Overview** with:
• **Languages:** main language(s) + 3 key phrases  
• **Currency:** local + conversion to home currency  
• **Etiquette:** crucial norms  
• **Getting Around:** best transport methods  
• **Weather Summary:** temp range & rain/sun chances  
• **Connectivity:** roaming vs. local SIM vs. public Wi‑Fi  

**Format** in Markdown, with clear headings and bullet points.  
Produce a complete, self‑contained plan—no follow‑up questions.
`.trim();
}
